import AxiosAgent from './AxiosAgent';

/**
 * Restriction tiers, mirroring the overdue policy in
 * ops-toolkit/docs/rcic_client_account_payment_flow.md:
 *  0 = no restriction (no unpaid invoice, or the invoice is not yet due)
 *  1 = invoice overdue (reminder sent, no restrictions)
 *  2 = 7+ days overdue (second reminder sent, no restrictions)
 *  3 = 14+ days overdue (cannot accept new consultation requests)
 *  4 = 30+ days overdue (account suspended, operational features blocked)
 */
export interface CommissionRestrictionStatus {
    // ── Core fields (always present) ────────────────────────────────────────
    restrictionTier: number;
    isRestricted: boolean;        // true → tier 4 (account suspended)
    bookingRestricted?: boolean;  // true → tier 3+ (new bookings blocked)
    hasWarning: boolean;          // true whenever the dashboard reminder should show
    hasUnpaidInvoice?: boolean;   // true when at least one issued invoice is unpaid
    invoiceId: number;            // Primary invoice driving the restriction
    invoiceNumber: string;
    outstandingAmount: number;
    currency: string;
    dueDate: string;              // Original invoice due date (ISO)
    daysOverdue: number;          // Days since due date (0 if not yet overdue)
    message: string;              // Human-readable message from backend

    // ── Extended fields (optional — backend may omit on older API versions) ─
    accountStatus?: 'ACTIVE' | 'SUSPENDED';
    /** Days remaining until the suspension threshold is reached. */
    daysUntilSuspension?: number;
    /** ISO timestamp when the account was suspended. */
    suspensionDate?: string;
    /** Total number of unpaid invoices contributing to the restriction. */
    totalUnpaidInvoices?: number;
    /** Aggregate unpaid amount across all overdue invoices. */
    totalUnpaidAmount?: number;
    /** true when a pre-due reminder is active (invoice not yet due). */
    isPreDueWarning?: boolean;
    /** Days remaining until the invoice due date (pre-due state). */
    preDueDaysRemaining?: number;
}

/**
 * Commission accrued so far in the current, still-open billing period. This is
 * deliberately not an invoice — it has no invoice number and cannot be paid. It is
 * billed on the 1st of the following month, at which point it becomes an invoice.
 */
export interface CommissionPeriodAccrual {
    billingPeriod: string;
    totalAmount: number;
    currencyCode: string;
    transactionCount: number;
    expectedIssueDate: string;
    expectedDueDate: string;
    ledgerEntries?: unknown[];
}

class CommissionService {
    async getRestrictionStatus(): Promise<CommissionRestrictionStatus> {
        try {
            const response = await AxiosAgent.requests.get('/commission/restriction-status');
            return response?.data ?? {};
        } catch (error) {
            console.error('Error fetching commission restriction status:', error);
            throw error;
        }
    }

    async getCurrentPeriodAccrual(): Promise<CommissionPeriodAccrual | null> {
        try {
            const response = await AxiosAgent.requests.get('/commission/current-period-accrual');
            return response?.data ?? null;
        } catch (error) {
            console.error('Error fetching current commission period accrual:', error);
            return null;
        }
    }
}

export default new CommissionService();
